import React, {useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {useLocation, useNavigate} from 'react-router-dom';
import {Cloud, CloudOff, List, LogOut, TrendingUp} from 'lucide-react';
import {useAuth} from '../contexts/AuthContext';

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
`;

const TopBar = styled.div`
  background: linear-gradient(135deg, #1d388d 0%, #2d4da3 50%, #1d388d 100%);
  background-size: 200% 200%;
  animation: ${shimmer} 8s ease-in-out infinite;
  padding: 16px 32px;
  box-shadow: 0 2px 12px rgba(29, 56, 141, 0.4);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(45, 77, 163, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(168, 213, 255, 0.5) 50%, 
      transparent 100%
    );
    animation: ${pulse} 3s ease-in-out infinite;
  }
`;

const NavBar = styled.nav`
  background: rgba(23, 37, 84, 0.95);
  padding: 12px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(45, 77, 163, 0.2);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
  }
  
  &:hover img {
    animation: ${float} 2s ease-in-out infinite;
  }
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
  padding: 8px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 
              0 0 15px rgba(29, 56, 141, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 
                0 0 25px rgba(29, 56, 141, 0.4),
                inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transform: scale(1.05);
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MainTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3),
               0 0 20px rgba(168, 213, 255, 0.3);
  display: flex;
  align-items: center;
  gap: 12px;
  
  &::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #a8d5ff;
    border-radius: 50%;
    box-shadow: 0 0 10px #a8d5ff;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const SubTitle = styled.p`
  font-size: 13px;
  color: #a8d5ff;
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '●';
    color: #4ade80;
    font-size: 10px;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NavMenu = styled.div`
  display: flex;
  gap: 12px;
`;

const NavButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' 
    : 'rgba(255, 255, 255, 0.05)'};
  color: white;
  border: 1px solid ${props => props.$active 
    ? 'rgba(59, 130, 246, 0.5)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  font-size: 15px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.$active 
    ? '0 2px 8px rgba(37, 99, 235, 0.3)' 
    : 'none'};
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' 
      : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.$active 
      ? 'rgba(59, 130, 246, 0.6)' 
      : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$active 
      ? '0 4px 12px rgba(37, 99, 235, 0.4)' 
      : '0 2px 8px rgba(0, 0, 0, 0.2)'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  background: #4ade80;
  border-radius: 50%;
  box-shadow: 0 0 10px #4ade80, 0 0 20px rgba(74, 222, 128, 0.5);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const StatusText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const NetatmoButton = styled.button<{ $connected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: ${props => props.$connected 
    ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' 
    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'};
  color: white;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: ${props => props.$connected ? 'default' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: ${props => props.$connected ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$connected ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.3)'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
  border-radius: 10px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.5);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [netatmoConnected, setNetatmoConnected] = useState(false);
  const [checkingNetatmo, setCheckingNetatmo] = useState(true);

  useEffect(() => {
    // Check Netatmo connection status
    const checkNetatmoStatus = async () => {
      try {
        const response = await fetch('/api/netatmo/status');
        const data = await response.json();
        setNetatmoConnected(data.authenticated || false);
      } catch (error) {
        console.error('Error checking Netatmo status:', error);
        setNetatmoConnected(false);
      } finally {
        setCheckingNetatmo(false);
      }
    };

    checkNetatmoStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkNetatmoStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogoClick = () => {
    navigate('/devices');
  };

  const handleNetatmoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!netatmoConnected) {
      console.log('Redirecting to Netatmo auth...');
      // Use full URL to backend since window.location.href doesn't use the dev proxy
        window.location.href = process.env.NODE_ENV === 'development'
          ? 'http://localhost:8443/api/netatmo/auth'
          : '/api/netatmo/auth';
    } else {
      console.log('Already connected to Netatmo');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isDevicesPage = location.pathname === '/' || location.pathname === '/devices' || location.pathname.startsWith('/devices/');
  const isComparisonPage = location.pathname === '/comparison';

  return (
    <HeaderContainer>
      <TopBar>
        <LogoSection onClick={handleLogoClick}>
          <LogoImage src={`${process.env.PUBLIC_URL}/logo.png`} alt="Waldevar Floating PV" />
          <TitleSection>
            <MainTitle>Waldevar Floating - Metrics & Stats</MainTitle>
            <SubTitle>Real-time Maritime Sensor Monitoring</SubTitle>
          </TitleSection>
        </LogoSection>
        <RightSection>
          {!checkingNetatmo && (
            <NetatmoButton
              $connected={netatmoConnected}
              onClick={handleNetatmoClick}
              disabled={netatmoConnected}
              title={netatmoConnected ? 'Netatmo Connected' : 'Connect to Netatmo'}
              type="button"
            >
              {netatmoConnected ? <Cloud /> : <CloudOff />}
              <span>{netatmoConnected ? 'Netatmo Connected' : 'Connect Netatmo'}</span>
            </NetatmoButton>
          )}
          <StatusIndicator>
            <StatusDot />
            <StatusText>System Online</StatusText>
          </StatusIndicator>
          <LogoutButton onClick={handleLogout} title="Logout">
            <LogOut />
            <span>Logout</span>
          </LogoutButton>
        </RightSection>
      </TopBar>
      <NavBar>
        <NavMenu>
          <NavButton
            $active={isDevicesPage}
            onClick={() => navigate('/devices')}
          >
            <List />
            <span>Devices</span>
          </NavButton>
          <NavButton
            $active={isComparisonPage}
            onClick={() => navigate('/comparison')}
          >
            <TrendingUp />
            <span>Compare</span>
          </NavButton>
        </NavMenu>
      </NavBar>
    </HeaderContainer>
  );
};

export default Header;
